import { FC, useEffect, useRef, useSyncExternalStore } from "react"
import { useTranslation } from "react-i18next"
import { Locker } from "shared/locker"
import { LockerThingTypeMrn } from "shared/lockerData"
import { Reservation } from "shared/reservation"
import { ReservationThingTypeMrn } from "shared/reservationData"
import { useAppSelector } from "src/app/hooks"
import { useNotification } from "src/components/NotificationProvider"
import { selectThingsByThingTypeMrn } from "src/features/things/thingsSlice"
import { getLockerDisplayValues } from "src/hooks/useLockerDisplayValues"
import {
  claimAutoPrint,
  getAutoPrintQueueVersion,
  subscribeAutoPrintQueue,
  takeReadyAutoPrints,
} from "./autoPrintQueue"
import { printErrorMessageKey } from "./printErrors"
import { useDepositCodePrinting } from "./useDepositCodePrinting"

type ReceptionCodeAutoPrintWatcherProps = {
  tenantId?: string
}

/**
 * LOCKER-522 — prints the deposit code of a reservation this console just
 * created, as soon as the reservation carries one (`autoPrintQueue.ts`).
 *
 * Renders nothing. Mounted once in `OsLayout`, which also wraps the reservation
 * form, so the console that queues is the console that prints.
 *
 * A workstation whose Browser Print agent is unreachable is treated as "not a
 * printing workstation" and skips silently; one whose agent answers but
 * cannot print gets a warning, because that clerk expects a label.
 */
export const ReceptionCodeAutoPrintWatcher: FC<
  ReceptionCodeAutoPrintWatcherProps
> = ({ tenantId }) => {
  const { t } = useTranslation()
  const { showNotification } = useNotification()
  const { autoPrint, health, print } = useDepositCodePrinting(tenantId)
  const reservations = useAppSelector((s) =>
    selectThingsByThingTypeMrn(s, ReservationThingTypeMrn),
  ) as Reservation[]
  const lockers = useAppSelector((s) =>
    selectThingsByThingTypeMrn(s, LockerThingTypeMrn),
  ) as Locker[]
  // The store may already hold the code when the form queues the id, so a
  // queue change must re-run the check as well as a reservation change.
  const queueVersion = useSyncExternalStore(
    subscribeAutoPrintQueue,
    getAutoPrintQueueVersion,
  )

  // Read through a ref so a health poll or a locker refetch does not re-run
  // the check.
  const latest = useRef({ autoPrint, health, print, lockers })
  latest.current = { autoPrint, health, print, lockers }

  useEffect(() => {
    const ready = takeReadyAutoPrints(reservations)
    const { autoPrint, health, print, lockers } = latest.current
    if (!autoPrint || health.status === "unavailable") return

    for (const reservation of ready) {
      if (!claimAutoPrint(reservation.id)) continue
      const locker = lockers.find((l) => l.id === reservation.lockerId)
      // The drawer's display name, not the raw `name` (QA printed a raw id).
      print({
        reservation,
        lockerName: locker ? getLockerDisplayValues(locker).i18n.name : "",
      })
        .then(() =>
          showNotification(
            t("receptionCodeAutoPrinted", {
              number: reservation.sstReservation?.number ?? "",
            }),
            "success",
          ),
        )
        .catch((err) => {
          console.error("Deposit code auto-print failed", err)
          showNotification(t(printErrorMessageKey(err)), "warning")
        })
    }
  }, [reservations, queueVersion, showNotification, t])

  return null
}
